import { inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { escape, find } from 'lodash';
import { NotificationService } from '@core/providers/notification/notification.service';
import {
  I18nAvailableLanguage,
  I18nSupportedLanguage,
  I18nValidtionCoverage,
} from '@shared/interfaces/i18n.interface';
import { StatementTranslationFromBackend } from '@shared/interfaces/statements.interface';

@Service()
export class LanguageService {
  private translateService = inject(TranslateService);
  private notificationService = inject(NotificationService);

  private defaultLanguage: I18nSupportedLanguage | undefined;

  showMissingTranslationInAccount(accountUuid: string, lang: I18nAvailableLanguage): void {
    if (!lang.validationState) {
      return;
    }

    this.showErrorPopup(
      this.translateService.instant('SHARED.LANGUAGES.CANNOT_ACTIVATE_MISSING_TRANSLATION'),
      `${this.translateService.instant('SHARED.LANGUAGES.MISSING_TRANSLATION_INFO', {
        languageName: escape(lang.localizedLanguageName),
      })} ${this.getAccountPagesLinkList(accountUuid, lang.validationState.coverage)}`
    );
  }

  showMissingTranslationInVenue(venueUuid: string, lang: I18nAvailableLanguage): void {
    if (!lang.validationState) {
      return;
    }

    this.showErrorPopup(
      this.translateService.instant('SHARED.LANGUAGES.CANNOT_ACTIVATE_MISSING_TRANSLATION'),
      `${this.translateService.instant('SHARED.LANGUAGES.SECTION_ACTIVE_TEXT_MISSING_TRANS', {
        languageName: escape(lang.localizedLanguageName),
      })} ${this.getVenuePagesLinkList(venueUuid, lang.validationState.coverage)}`
    );
  }

  showCannotDeactivateLanguage(unitNames: string[], lang: I18nAvailableLanguage): void {
    this.showErrorPopup(
      this.translateService.instant('SHARED.LANGUAGES.CANNOT_DEACTIVATE_HEADER'),
      `${this.translateService.instant('SHARED.LANGUAGES.CANNOT_DEACTIVATE_TEXT', {
        languageName: escape(lang.localizedLanguageName),
      })} ${this.getUnitNameList(unitNames)}`
    );
  }

  getDefaultLanguage(): I18nSupportedLanguage | undefined {
    return this.defaultLanguage;
  }

  setDefaultLanguage(language: I18nSupportedLanguage): void {
    this.defaultLanguage = language;
  }

  getTranslationObject(
    translations: StatementTranslationFromBackend[],
    lang: string
  ): StatementTranslationFromBackend | null {
    if (!translations || translations.length === 0) {
      return null;
    }

    return find(translations, { lang }) ?? null;
  }

  getTranslation(translations: StatementTranslationFromBackend[], lang: string): string {
    const selectedTranslation = this.getTranslationObject(translations, lang);

    return selectedTranslation ? selectedTranslation.value : '';
  }

  private getVenuePagesLinkList(venueUid: string, coverage: I18nValidtionCoverage): string {
    let content = '<ul>';
    if (!coverage.portal.isTranslated) {
      content += `<li>${this.getAlertItemLink(
        `/venue/${venueUid}/settings/features/portal/configuration`,
        this.translateService.instant('SHARED.SYSTEM.FLEXIBLE_PORTAL')
      )}</li>`;
    }

    content += '</ul>';

    return content;
  }

  private getAccountPagesLinkList(accountUuid: string, coverage: I18nValidtionCoverage): string {
    let content = '<ul>';

    if (!coverage.foodbackQuestion.isTranslated) {
      content += `<li>${this.getAlertItemLink(
        `/account/${accountUuid}/settings/foodback-questions`,
        this.translateService.instant('SHARED.SYSTEM.FOODBACK_QUESTIONS')
      )}</li>`;
    }
    if (!coverage.oneMoreQuestion.isTranslated) {
      content += `<li>${this.getAlertItemLink(
        `/account/${accountUuid}/settings/one-more-question`,
        this.translateService.instant('SHARED.SYSTEM.ONE_MORE_QUESTION')
      )}</li>`;
    }
    if (!coverage.campaign.isTranslated) {
      content += `<li>${this.getAlertItemLink(
        `/account/${accountUuid}/settings/campaigns`,
        this.translateService.instant('SHARED.SYSTEM.CAMPAIGNS')
      )}</li>`;
    }

    content += '</ul>';

    return content;
  }

  private getUnitNameList(unitNames: string[]): string {
    return `<ul>${unitNames.map(name => `<li>${escape(name)}</li>`).join('')}</ul>`;
  }

  private getAlertItemLink(link: string, transKeyHeader: string): string {
    return `<a href="${link}" target="_blank" rel="noopener noreferrer">${this.translateService.instant(transKeyHeader)}</a>`;
  }

  private showErrorPopup(header: string, content: string): void {
    this.notificationService.displaySwalPopup(
      header,
      `<div class="Alert-content">${content}</div>`
    );
  }
}
